/**
 * Groups editor (W2-07; DESIGN.md §5.5 "Groups": create, rename, colour; exported as `judgeLineGroup` + each line's
 * `Group`, LINE-12). Lists the groups in export position with their line counts: rename (Enter / blur), colour
 * (editor-only tint), move up / down (the position in `judgeLineGroup`), delete (lines go back to "Default"), and
 * "Show only these lines" (the panel's group filter). A new group can take the selected lines at once. Every change
 * is one undoable step.
 */

import { useState } from 'react';
import type { AppServices } from '../../app/registry/types';
import { Core } from '../../core/commands';
import type { ChartDoc, Line, LineGroup } from '../../core/model/types';
import type { RGB } from '../../core/util/types';
import { useT } from '../../i18n/useT';
import type { DocStore } from '../../state/types';
import { Button } from '../../ui/Button';
import { Checkbox } from '../../ui/Checkbox';
import { ColorField } from '../../ui/ColorField';
import { Dialog } from '../../ui/Dialog';
import { IconButton } from '../../ui/IconButton';
import { sameItems, useDocValue, useEditorValue } from './hooks';
import { createGroup, targetLines, transact } from './lineActions';
import { m } from './lines.messages';
import { NO_FILTERS, NO_GROUP, type GroupFilterValue } from './lineModel';
import { linesUi } from './linesUi';
import s from './Lines.module.css';

/** '#rrggbb' -> RGB (null for anything else). */
export function hexToRgb(hex: string | null): RGB | null {
  if (hex === null) return null;
  const mm = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (mm === null) return null;
  return [parseInt(mm[1] ?? '0', 16), parseInt(mm[2] ?? '0', 16), parseInt(mm[3] ?? '0', 16)];
}

export function rgbToHex(c: RGB): string {
  return `#${c
    .map((v) =>
      Math.max(0, Math.min(255, Math.round(v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

const groupRecords = (d: ChartDoc): LineGroup[] => [...d.groups.values()];
const lineRecords = (d: ChartDoc): Line[] => [...d.lines.values()];

function countByGroup(lines: readonly Line[]): Map<GroupFilterValue, number> {
  const out = new Map<GroupFilterValue, number>();
  for (const l of lines) {
    const g: GroupFilterValue = l.group ?? NO_GROUP;
    out.set(g, (out.get(g) ?? 0) + 1);
  }
  return out;
}

export interface GroupsEditorProps {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly onClose: () => void;
}

export function GroupsEditor(props: GroupsEditorProps) {
  const { services, store } = props;
  const t = useT();
  const groups = useDocValue(store, groupRecords, ['groups'], sameItems);
  const lines = useDocValue(store, lineRecords, ['lines'], sameItems);
  const selectedCount = useEditorValue(services, (st) => st.selection.lines.size);
  const [name, setName] = useState('');
  const [withSelection, setWithSelection] = useState(true);
  const sorted = [...groups].sort((a, b) => a.order - b.order);
  const counts = countByGroup(lines);

  const patch = (
    g: LineGroup,
    p: { readonly name?: string; readonly color?: string | null; readonly order?: number },
  ): void => {
    transact(services, { label: { key: m.txEditGroup, params: { name: g.name } }, command: 'lines.groups' }, (tx) => {
      Core.lines.patchGroup(tx, { id: g.id, patch: p });
    });
  };

  const remove = async (g: LineGroup): Promise<void> => {
    const n = counts.get(g.id) ?? 0;
    if (n > 0) {
      const ok = await services.ui.confirm({
        title: { key: m.groupDeleteTitle, params: { name: g.name } },
        body: { key: m.groupDeleteBody, params: { count: n } },
        confirm: { key: m.groupDeleteConfirm },
        danger: true,
      });
      if (!ok) return;
    }
    transact(
      services,
      { label: { key: m.txDeleteGroup, params: { name: g.name } }, command: 'lines.groups' },
      (tx) => {
        Core.lines.deleteGroup(tx, { id: g.id });
      },
      { success: { key: m.groupDeleted, params: { name: g.name } } },
    );
  };

  const showOnly = (g: GroupFilterValue): void => {
    linesUi(services).set({ filters: { ...NO_FILTERS, groups: new Set([g]) }, modal: null });
    services.ui.showPanel('lines');
  };

  const add = (): void => {
    const trimmed = name.trim();
    if (trimmed === '') return;
    const ids = withSelection && selectedCount > 0 ? targetLines(services) : [];
    if (createGroup(services, trimmed, ids) !== null) setName('');
  };

  const move = (g: LineGroup, dir: -1 | 1): void => {
    const i = sorted.indexOf(g);
    const other = sorted[i + dir];
    if (other === undefined) return;
    patch(g, { order: Math.max(1, other.order) });
  };

  const row = (g: LineGroup, i: number) => {
    const rgb = hexToRgb(g.color);
    return (
      <li key={g.id} className={s.groupRow}>
        <ColorField
          label={<span className={s.visuallyHidden}>{t(m.groupColour, { name: g.name })}</span>}
          value={rgb === null ? null : { kind: 'value', value: rgb }}
          allowNone
          onCommit={(c) => {
            patch(g, { color: c === null ? null : rgbToHex(c) });
          }}
        />
        <input
          key={g.name}
          className={s.input}
          defaultValue={g.name}
          aria-label={t(m.groupNameOf, { name: g.name })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            } else if (e.key === 'Escape') {
              e.currentTarget.value = g.name;
            }
          }}
          onBlur={(e) => {
            const next = e.currentTarget.value.trim();
            if (next === '') e.currentTarget.value = g.name;
            else if (next !== g.name) patch(g, { name: next });
          }}
        />
        <span className={s.groupCount}>{t(m.groupLines, { count: counts.get(g.id) ?? 0 })}</span>
        <span className={s.groupActions}>
          <IconButton
            size="sm"
            icon="Filter"
            label={{ key: m.groupShowOnly, params: { name: g.name } }}
            onClick={() => {
              showOnly(g.id);
            }}
          />
          <IconButton
            size="sm"
            icon="ArrowUp"
            label={{ key: m.groupMoveUp, params: { name: g.name } }}
            disabledReason={i === 0 ? { key: m.groupFirst } : null}
            onClick={() => {
              move(g, -1);
            }}
          />
          <IconButton
            size="sm"
            icon="ArrowDown"
            label={{ key: m.groupMoveDown, params: { name: g.name } }}
            disabledReason={i === sorted.length - 1 ? { key: m.groupLast } : null}
            onClick={() => {
              move(g, 1);
            }}
          />
          <IconButton
            size="sm"
            icon="Trash2"
            label={{ key: m.groupDelete, params: { name: g.name } }}
            onClick={() => {
              void remove(g);
            }}
          />
        </span>
      </li>
    );
  };

  return (
    <Dialog
      open={true}
      size="md"
      title={t(m.groupsTitle)}
      description={t(m.groupsDescription)}
      onClose={props.onClose}
      footer={
        <Button variant="primary" onClick={props.onClose}>
          {t(m.done)}
        </Button>
      }
    >
      <div className={s.form}>
        <ul className={s.groupList} aria-label={t(m.groupsTitle)}>
          <li className={s.groupRow}>
            <span />
            <span>{t(m.groupDefault)}</span>
            <span className={s.groupCount}>{t(m.groupLines, { count: counts.get(NO_GROUP) ?? 0 })}</span>
            <span className={s.groupActions}>
              <IconButton
                size="sm"
                icon="Filter"
                label={{ key: m.groupShowOnly, params: { name: t(m.groupDefault) } }}
                onClick={() => {
                  showOnly(NO_GROUP);
                }}
              />
            </span>
          </li>
          {sorted.map(row)}
        </ul>
        {sorted.length === 0 && <p className={s.help}>{t(m.groupsEmpty)}</p>}
        <div className={s.newGroup}>
          <div className={s.fieldRow}>
            <label className={s.label} htmlFor="lines-new-group">
              {t(m.newGroupName)}
            </label>
            <input
              id="lines-new-group"
              className={s.input}
              value={name}
              placeholder={t(m.newGroupPlaceholder)}
              onChange={(e) => {
                setName(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  add();
                }
              }}
            />
          </div>
          <Button
            icon="FolderPlus"
            disabledReason={name.trim() === '' ? { key: m.newGroupNeedsName } : null}
            onClick={add}
          >
            {t(m.newGroupAdd)}
          </Button>
        </div>
        {selectedCount > 0 && (
          <Checkbox
            label={t(m.newGroupWithSelection, { count: selectedCount })}
            checked={withSelection}
            onChange={setWithSelection}
          />
        )}
      </div>
    </Dialog>
  );
}
